import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle, XCircle, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { youtubeApi } from '@/lib/api/services/youtube';

export default function YouTubeCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Connecting your YouTube channel...');

  useEffect(() => {
    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCallback = async () => {
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    const isPopup = window.opener && window.opener !== window;

    if (error) {
      setStatus('error');
      setMessage(errorDescription || error || 'Authorization was denied or cancelled');
      if (isPopup) {
        window.opener.postMessage(
          {
            type: 'YOUTUBE_OAUTH_ERROR',
            error: errorDescription || error || 'Authorization was denied or cancelled',
          },
          window.location.origin,
        );
        window.close();
      }
      return;
    }

    if (!code) {
      setStatus('error');
      setMessage('No authorization code received');
      if (isPopup) {
        window.opener.postMessage(
          { type: 'YOUTUBE_OAUTH_ERROR', error: 'No authorization code received' },
          window.location.origin,
        );
        window.close();
      }
      return;
    }

    try {
      const storedRedirectUri = localStorage.getItem('youtube_oauth_redirect_uri');
      const redirectUri = storedRedirectUri || `${window.location.origin}/youtube-callback`;
      if (storedRedirectUri) localStorage.removeItem('youtube_oauth_redirect_uri');

      const businessAccountId = localStorage.getItem('selectedBusinessAccountId') || null;
      const workspaceId = localStorage.getItem('selectedWorkspaceId') || null;
      const res = await youtubeApi.exchangeCode({ code, redirect_uri: redirectUri, business_account_id: businessAccountId, workspace_id: workspaceId });

      if (!res.ok) {
        throw ApiError.fromResult(res);
      }

      setStatus('success');
      const title = res.data.yt_channel_title || 'your YouTube channel';
      setMessage(
        res.data.isNew
          ? `Successfully connected ${title}!`
          : `Reconnected ${title} — token refreshed.`,
      );

      if (isPopup) {
        window.opener.postMessage(
          { type: 'YOUTUBE_OAUTH_SUCCESS', data: res.data },
          window.location.origin,
        );
        setTimeout(() => window.close(), 1000);
      } else {
        setTimeout(() => navigate('/home/social-media/youtube'), 1500);
      }
    } catch (err: any) {
      setStatus('error');
      const errorMessage = err?.message || 'Failed to connect YouTube channel';
      setMessage(errorMessage);
      if (isPopup) {
        window.opener.postMessage(
          { type: 'YOUTUBE_OAUTH_ERROR', error: errorMessage },
          window.location.origin,
        );
        setTimeout(() => window.close(), 2000);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-rose-50 to-orange-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <div className="bg-gradient-to-br from-red-500 to-red-700 p-4 rounded-full text-white inline-block">
              <Youtube className="h-8 w-8" />
            </div>
          </div>
          <CardTitle>YouTube Connection</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'loading' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin mx-auto text-red-500" />
              <p className="text-muted-foreground">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
              <p className="text-lg font-semibold text-green-700">{message}</p>
              <p className="text-sm text-muted-foreground">
                {window.opener && window.opener !== window ? 'Closing popup...' : 'Redirecting...'}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-12 w-12 mx-auto text-red-500" />
              <p className="text-lg font-semibold text-red-700">Connection Failed</p>
              <p className="text-sm text-muted-foreground whitespace-pre-line">{message}</p>
              <div className="flex gap-2 justify-center mt-4">
                <Button onClick={() => navigate('/home/social-media/youtube')} variant="outline">
                  Go Back
                </Button>
                <Button onClick={handleCallback}>Try Again</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
